/* SwarmBoard — the Agent IO swarm picture with a run picker. Follows the
   org's live coordinator runs (jobs/{id}/swarm) and falls back to the
   scripted replay when nothing is running. A run with pipeline.call fan-out
   has one swarm doc per coordinator per call; they show as tabs. */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SwarmView from './SwarmView';
import { useOrgSwarmJobs, useSwarmDocs, useSwarmReplay } from '../data/swarm';
import { LAUNCH_SWARM, LAUNCH_SWARM_META } from '../data/swarmDemo';

const ACTIVE = new Set(['queued', 'planning', 'running', 'stitching']);
const ago = (t) => {
  if (!t) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export default function SwarmBoard({ org, compact = false }) {
  const jobs = useOrgSwarmJobs(org?.id);
  const [choice, setChoice] = useState(null); // job id | 'demo' | null (= auto)
  const auto = useMemo(() => {
    if (!jobs?.length) return 'demo';
    const live = jobs.find((j) => ACTIVE.has(j.status));
    return (live || jobs[0]).id;
  }, [jobs]);
  const selected = choice || auto;
  const jobId = selected === 'demo' ? null : selected;
  const job = jobs?.find((j) => j.id === jobId) || null;
  const docs = useSwarmDocs(jobId);
  const [docId, setDocId] = useState(null);
  useEffect(() => { setDocId(null); }, [jobId]);
  const doc = docs.find((d) => d.id === docId) || docs.find((d) => d.status === 'running') || docs[docs.length - 1] || null;
  const demo = useSwarmReplay(LAUNCH_SWARM, { enabled: selected === 'demo' });

  const isDemo = selected === 'demo';
  const pipelineLink = job?.spec?.workspaceId && job?.spec?.pipelineId
    ? `/orgs/${org.id}/ws/${job.spec.workspaceId}/pipelines/${job.spec.pipelineId}` : null;

  return (
    <div className="swb">
      <div className="swb-bar">
        <label className="swb-pick">
          <span>Showing</span>
          <select value={selected} onChange={(e) => setChoice(e.target.value)}>
            <option value="demo">Replay · {LAUNCH_SWARM_META.label} (example)</option>
            {(jobs || []).map((j) => (
              <option key={j.id} value={j.id}>
                {ACTIVE.has(j.status) ? '● live · ' : ''}{(j.title || j.id).replace(/^pipeline: /, '')} · {j.status} · {ago(j.createdAt)}
              </option>
            ))}
          </select>
        </label>
        {docs.length > 1 && (
          <div className="swb-tabs" role="tablist">
            {docs.map((d) => (
              <button
                key={d.id}
                type="button"
                role="tab"
                aria-selected={doc?.id === d.id}
                className={`swb-tab ${doc?.id === d.id ? 'is-on' : ''} is-${d.status}`}
                onClick={() => setDocId(d.id)}
              >
                {d.label || d.block}{d.item ? ` · ${d.item}/${d.of || '?'}` : ''}{d.depth ? ` · d${d.depth}` : ''}
              </button>
            ))}
          </div>
        )}
        <span className="swb-spacer" />
        {pipelineLink && <Link to={pipelineLink} className="mf-chip">open pipeline</Link>}
        {isDemo && <Link to={`/orgs/${org.id}/workflows`} className="mf-chip">run one from Examples</Link>}
      </div>
      <SwarmView
        doc={isDemo ? demo : doc}
        title={isDemo ? LAUNCH_SWARM_META.label : (doc?.label || job?.title || 'Coordinator')}
        subtitle={isDemo ? LAUNCH_SWARM_META.pipeline : (job ? `${job.status} · ${ago(job.createdAt)}` : '')}
        tag={isDemo ? 'replay' : 'live'}
        compact={compact}
      />
      {isDemo && <p className="cs-help swb-note">{LAUNCH_SWARM_META.note} When one of your pipelines runs a Coordinator block, it appears here live.</p>}
    </div>
  );
}
